import { useEffect, useState } from 'react';
import { Sports } from '@gin-betting/constants';
import core, { App } from '@gin-betting/core';
import { get, toNumber } from 'lodash';
import { createSelector } from 'reselect';

import { CONFIG_BY_SPORTS } from 'scoreMetaConfig';
import {
    selectFilteredScores,
    selectMatchTime,
    selectTimerRunning,
} from 'selectors';
import { convertSecondsToTimeFormat, getMapNameFromMeta } from 'utils';
import useMemoSelector from './useMemoSelector';

const ONE_SECOND = 1000;
const TICK_INTERVAL = 1000;
const TICK_TIMEOUT = 60 * 1000;

interface Input {
    matchId: string;
}

interface Output {
    currentMatchPart: string | undefined;
    matchTimeToShow: string | null;
}

const {
    entities: {
        selectors: { selectMatch },
    },
} = core;

const makeSelectProps = () =>
    createSelector(
        selectMatch,
        selectMatchTime,
        selectTimerRunning,
        selectFilteredScores,
        (match, matchTime, timerRunning, { currentMatchPart }) => {
            return {
                sportId: match?.sportId || '',
                shouldShowTime: matchTime !== null,
                numericalMatchTime: toNumber(matchTime) || 0,
                isTimerRunning: timerRunning === '1',
                currentMatchPart,
            };
        }
    );

function useMatchTime({ matchId }: Input): Output {
    const {
        numericalMatchTime,
        isTimerRunning,
        currentMatchPart,
        shouldShowTime,
        sportId,
    } = useMemoSelector(makeSelectProps(), { matchId });

    const config = get(CONFIG_BY_SPORTS, sportId, undefined);

    const isDecrement = config?.isTimeCountdown;

    const [time, changeTime] = useState(numericalMatchTime);

    useEffect(() => changeTime(numericalMatchTime), [numericalMatchTime]);

    useEffect(() => {
        if (!isTimerRunning) return;

        const addition = isDecrement ? -TICK_INTERVAL : TICK_INTERVAL;

        const timerId = setInterval(() => {
            changeTime(prev => prev + addition);
        }, TICK_INTERVAL);

        const tickTimeoutId = setTimeout(
            () => clearInterval(timerId),
            TICK_TIMEOUT
        );

        return () => {
            clearInterval(timerId);
            clearTimeout(tickTimeoutId);
        };
    }, [isTimerRunning, isDecrement]);

    const finalTime = config?.isTimeAbsolute
        ? Math.abs(time)
        : Math.max(time, 0);

    const timeInSeconds = Math.trunc(finalTime / ONE_SECOND);

    const matchTimeToShow = config?.isTimeInSeconds
        ? convertSecondsToTimeFormat(timeInSeconds, [60, 1]) // NOTE: other option is Math.ceil,depends on back-office
        : `${Math.trunc((finalTime + 59000) / 60000)}\``;

    return {
        currentMatchPart:
            sportId === Sports.ESPORTS_COUNTER_STRIKE &&
            currentMatchPart?.type === App.MetaTypes.TYPE_MAP
                ? getMapNameFromMeta({
                      map: currentMatchPart?.text,
                      format: 'long',
                  })
                : currentMatchPart?.text,
        matchTimeToShow:
            shouldShowTime && !currentMatchPart?.isTimeDisabled
                ? matchTimeToShow
                : null,
    };
}

export default useMatchTime;
